import { FormControlProps, OutlinedInput, OutlinedInputProps, SelectProps, Skeleton } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  ConditionCategory as ConditionCategoryEnum,
  type ConditionOperator as ConditionOperatorEnum,
} from "@sinch/core/entities/serverEnums";
import type { ConditionCategory, ConditionOperator, ConditionType } from "@sinch/types/sinch.types";
import { isNil } from "ramda";
import { isNotNil } from "ramda-adjunct";
import React, { FC, Suspense, useMemo } from "react";
import { FormControlWrapper, InputWrapperProps } from "@sinch/components";
import { ConditionInputInputs } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/ConditionInputInputs";

export type ConditionInputProps = Omit<OutlinedInputProps, "onChange" | "value"> &
  Omit<InputWrapperProps, "slots" | "slotProps"> & {
    FormControlProps?: FormControlProps;
    onChange: (val: ConditionInputValue) => void;
    value: ConditionInputValue | null;
  };

export type ConditionValue = string | number | Date | Record<string, any> | (string | number | Date)[] | null;

export interface ConditionInputValue {
  category: ConditionCategory | ConditionCategoryEnum | null;
  operator: ConditionOperator | ConditionOperatorEnum | null;
  subjectId: string | null;
  type: ConditionType | null;
  value: ConditionValue;
}
export const ConditionInput: FC<ConditionInputProps> = ({
  label,
  FormHelperTextProps: formHelperTextProps,
  FormControlProps: formControlProps,
  helperText,
  errorMessage,
  error,
  required,
  infoTooltip,
  fullWidth,
  size,
  onChange,
  ...props
}) => {
  const inputRefs = React.useRef<(HTMLInputElement | null)[]>([null, null, null]);
  const [focusedIndex, setFocusedIndex] = React.useState(-1);
  const isIgnored: boolean = isNil(props.value);

  const slotProps = useMemo<OutlinedInputProps["slotProps"]>(
    () => ({
      input: {
        onChangeValue: (e) => {
          onChange(e as ConditionInputValue);
        },
        onFocusChange: setFocusedIndex,
        inputRefs: inputRefs,
        empty: isNotNil(props.value) && isNil(props.value?.subjectId) && isNil(props.value?.type),
      },
    }),
    [onChange, props.value, setFocusedIndex]
  );

  return (
    <Suspense fallback={<ConditionInputPlaceholder />}>
      <FormControlWrapper
        error={error}
        errorMessage={errorMessage}
        focused={focusedIndex > -1}
        FormHelperTextProps={formHelperTextProps}
        helperText={helperText}
        infoTooltip={infoTooltip}
        label={label}
        required={required}
        {...formControlProps}
        fullWidth={fullWidth}
      >
        <ConditionOutlinedInput
          error={error}
          fullWidth={fullWidth}
          isIgnored={isIgnored}
          required={required}
          size={size}
          slotProps={slotProps}
          slots={{ input: ConditionInputInputs }}
          {...props}
        />
      </FormControlWrapper>
    </Suspense>
  );
};

const ConditionOutlinedInput = styled(OutlinedInput, {
  shouldForwardProp: (prop) => prop !== "isIgnored",
})<OutlinedInputProps & { isIgnored?: boolean }>(({ theme, size, isIgnored }) => ({
  height: size === "small" ? theme.shape.inputSmallHeight : theme.shape.inputMediumHeight,
  backgroundColor: isIgnored ? "transparent" : undefined,
  "& .MuiOutlinedInput-notchedOutline": isIgnored
    ? {
        borderColor: theme.palette.grey[500],
      }
    : {},
}));

export type ConditionSelectProps<TVal> = Omit<SelectProps<string>, "value" | "onChange" | "type"> & {
  onOpenChange: (open: boolean) => void;
  onChange: (val: TVal | null) => void;
  value: TVal | null;
};

const ConditionInputPlaceholder = () => <Skeleton sx={{ height: "100%", bgcolor: "grey.300" }} variant="rounded" />;
